'use client';

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, RotateCcw, User, CalendarCheck, Ticket } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabaseClient';
import { useBajaPerfil } from '@/components/hooks/useBajaPerfil';

const LS_RETURN_TO = 'after_register_return_to';

type PerfilInactivo = {
  id_perfil: string;
  ruta_panel: string | null;
  fecha_baja: string | null;
};

/**
 * Pantalla de recuperación del perfil de cliente.
 *
 * Se llega desde la tarjeta en gris de /profiles, o desde OAuthCallback cuando
 * alguien con el perfil dado de baja inicia sesión o intenta reservar.
 *
 * Solo el perfil de cliente puede darse de baja y reactivarse a sí mismo: al
 * trabajador lo gestiona su empresa y la baja de un negocio se tramita por
 * correo.
 *
 * El perfil inactivo no aparece en vista_usuarios_detalle (filtra activo), por
 * eso se consulta vista_usuarios_perfiles_todos.
 */
const ReactivateProfile = () => {
  const navigate = useNavigate();
  const { reactivarPerfil, loading } = useBajaPerfil();

  const [perfil, setPerfil] = useState<PerfilInactivo | null>(null);
  const [cargando, setCargando] = useState(true);

  // Origen de la navegación (p. ej. /client-booking/xxx). Si viene de reservar
  // en una empresa, la reactivación es un paso intermedio y hay que devolverlo
  // ahí, no a /profiles. Se lee una sola vez al montar: handleReactivar lo
  // borra de localStorage al usarlo.
  const returnTo = useMemo(() => localStorage.getItem(LS_RETURN_TO), []);

  useEffect(() => {
    let cancelado = false;

    const cargar = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user) {
        navigate('/', { replace: true });
        return;
      }

      const { data, error } = await supabase
        .from('vista_usuarios_perfiles_todos')
        .select('id_perfil, ruta_panel, fecha_baja')
        .eq('id_usuario', session.user.id)
        .eq('perfil', 'cliente')
        .eq('activo', false)
        .not('fecha_baja', 'is', null)
        .limit(1)
        .maybeSingle();

      if (cancelado) return;

      // Sin perfil de cliente dado de baja no hay nada que hacer aquí.
      if (error || !data) {
        navigate(returnTo || '/profiles', { replace: true });
        return;
      }

      setPerfil(data as PerfilInactivo);
      setCargando(false);
    };

    cargar();

    return () => {
      cancelado = true;
    };
  }, [navigate, returnTo]);

  const handleReactivar = async () => {
    if (!perfil) return;

    const res = await reactivarPerfil(perfil.id_perfil);

    if (!res.ok) {
      toast.error(res.error);
      return;
    }

    toast.success('Perfil reactivado. Bienvenido de nuevo.');

    if (returnTo) {
      localStorage.removeItem(LS_RETURN_TO);

      // ClientBooking cachea los perfiles en useProfiles y ya escucha este
      // evento para hacer refetch. Sin dispararlo, al volver seguiría viendo
      // al usuario como "no cliente" y le mostraría otra vez el gate de acceso.
      window.dispatchEvent(new Event('clientRegistroCompletado'));

      navigate(returnTo, { replace: true });
      return;
    }

    navigate('/profiles', { replace: true });
  };

  const handleVolver = () => {
    navigate(returnTo || '/profiles', { replace: true });
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#f4f6f9] dark:bg-black font-sans">
      {/* HERO — mismo lenguaje visual que la pantalla de perfiles */}
      <div className="relative overflow-hidden pb-32">
        <div
          className="absolute inset-0 dark:hidden"
          style={{
            background: `
              radial-gradient(ellipse at 15% 20%, rgba(40,142,127,0.28) 0%, transparent 55%),
              radial-gradient(ellipse at 85% 70%, rgba(26,77,122,0.35) 0%, transparent 55%),
              linear-gradient(160deg, #0c2942 0%, #103254 45%, #1a4d7a 100%)
            `,
          }}
        />
        <div
          className="absolute inset-0 hidden dark:block"
          style={{
            background: `
              radial-gradient(ellipse at 15% 20%, rgba(40,142,127,0.3) 0%, transparent 55%),
              radial-gradient(ellipse at 85% 70%, rgba(16,50,84,0.5) 0%, transparent 55%),
              linear-gradient(160deg, #050807 0%, #0a1a16 30%, #103254 70%, #000000 100%)
            `,
          }}
        />

        <motion.div
          className="pointer-events-none absolute -top-16 -right-10 h-80 w-80 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(40,142,127,0.22) 0%, transparent 70%)',
            filter: 'blur(50px)',
          }}
          animate={{ scale: [1, 1.12, 1], opacity: [0.5, 0.9, 0.5] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="pointer-events-none absolute -bottom-10 -left-16 h-64 w-64 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)',
            filter: 'blur(50px)',
          }}
          animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.75, 0.4] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
        />

        <header className="relative z-10 mx-auto flex w-full max-w-5xl items-center justify-between px-6 py-5">
          <button
            type="button"
            onClick={handleVolver}
            className="flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm text-white backdrop-blur-sm transition-colors hover:bg-white/20"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline font-medium">
              {returnTo ? 'Volver' : 'Mis perfiles'}
            </span>
          </button>

          <img
            src="https://ijolbckmgiwomizpzrae.supabase.co/storage/v1/object/public/bookoll/logos/logobookoll.png"
            alt="Bookoll Logo"
            className="h-10 sm:h-11 object-contain"
          />

          <span aria-hidden className="w-[44px] sm:w-[124px]" />
        </header>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="relative z-10 mx-auto mt-6 max-w-2xl px-6 text-center"
        >
          <span className="mb-3 inline-block text-xs font-semibold uppercase tracking-[0.14em] text-white/60">
            Perfil desactivado
          </span>
          <h1 className="text-3xl font-extrabold leading-tight tracking-tight text-white">
            Recupera tu perfil de cliente
          </h1>
          <p className="mx-auto mt-3 max-w-lg text-[15px] leading-relaxed text-white/70">
            {returnTo
              ? 'Diste de baja este perfil. Reactívalo y sigues con tu reserva donde lo dejaste.'
              : 'Diste de baja este perfil, pero no hemos eliminado nada. Puedes volver a activarlo ahora mismo.'}
          </p>
        </motion.div>

        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none pointer-events-none">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="w-full h-14 sm:h-20">
            <path
              d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z"
              className="fill-[#f4f6f9] dark:fill-black"
            />
          </svg>
        </div>
      </div>

      {/* CONTENIDO — solapa el hero, igual que en /profiles */}
      <main className="relative z-10 mx-auto -mt-24 w-full max-w-xl flex-1 px-6 pb-16">
        {cargando ? (
          <div className="flex h-48 items-center justify-center">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#103254]/15 border-t-[#103254] dark:border-blue-400/20 dark:border-t-blue-400" />
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="rounded-2xl border border-[#e6eaf0] bg-white p-6 shadow-[0_10px_30px_-18px_rgba(16,50,84,0.35)] dark:border-white/10 dark:bg-[#1c1c1e]"
          >
            <div className="flex items-center gap-3.5">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#eef0f5] text-[#103254] dark:bg-white/10 dark:text-[#9db4cc]">
                <User className="h-6 w-6" />
              </div>
              <div className="min-w-0">
                <h2 className="truncate text-base font-bold text-[#0f1e2e] dark:text-white">
                  Perfil de cliente
                </h2>
                <p className="text-[13px] text-gray-500 dark:text-gray-400">
                  Desactivado
                </p>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                Al reactivarlo recuperas:
              </p>

              <div className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-400">
                <CalendarCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#288E7F]" />
                Tus citas y todo tu historial de reservas.
              </div>
              <div className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-400">
                <Ticket className="mt-0.5 h-4 w-4 shrink-0 text-[#288E7F]" />
                Tus bonos comprados y los usos que te quedaran.
              </div>
              <div className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-400">
                <User className="mt-0.5 h-4 w-4 shrink-0 text-[#288E7F]" />
                El acceso a tu panel y a tus datos personales.
              </div>
            </div>

            <Button
              onClick={handleReactivar}
              disabled={loading}
              className="mt-7 w-full rounded-xl py-3 font-bold text-white border-0 hover:brightness-110 disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #288E7F 0%, #1e7a6e 100%)' }}
            >
              {loading ? (
                'Reactivando...'
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <RotateCcw className="h-4 w-4" />
                  {returnTo ? 'Reactivar y continuar' : 'Reactivar mi perfil'}
                </span>
              )}
            </Button>

            <p className="mt-5 text-center text-xs leading-relaxed text-gray-400 dark:text-gray-500">
              Si prefieres que eliminemos por completo tu cuenta y tus datos,
              escríbenos a{' '}
              <a
                href="mailto:info@bookoll.com"
                className="font-semibold text-[#103254] hover:opacity-80 dark:text-white"
              >
                info@bookoll.com
              </a>
              .
            </p>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default ReactivateProfile;